/**
 * Free Write Exercise
 * User writes a free-form response within word count constraints.
 * Score = 100 if minWords met. AI evaluation is a future backend feature.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { TouchableOpacity, TextInput } from 'react-native';
import { Box, Text, VStack } from '@gluestack-ui/themed';
import { colors, FS_XS, FS_SM } from '../../theme';
import { useKeyboardShortcut } from '../../hooks/useKeyboardShortcut';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

interface FreeWriteExerciseProps {
  prompt: string;
  writingType: string;
  minWords: number;
  maxWords: number;
  sampleAnswer?: string;
  scaffolding?: string[];
  targetVocabulary?: string[];
  hint?: string;
  onComplete: (correct: boolean, score: number) => void;
}

export const FreeWriteExercise = React.memo(function FreeWriteExercise({
  prompt,
  writingType,
  minWords,
  maxWords,
  sampleAnswer,
  scaffolding,
  targetVocabulary,
  hint,
  onComplete,
}: FreeWriteExerciseProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();

  const [userText, setUserText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [showScaffolding, setShowScaffolding] = useState(false);
  const [showSample, setShowSample] = useState(false);

  const wordCount = useMemo(() => {
    const trimmed = userText.trim();
    if (trimmed.length === 0) return 0;
    return trimmed.split(/\s+/).length;
  }, [userText]);

  const meetsMinWords = wordCount >= minWords;
  const exceedsMaxWords = wordCount > maxWords;

  const handleSubmit = useCallback(() => {
    if (submitted || !meetsMinWords) return;
    setSubmitted(true);
    onComplete(true, 100);
  }, [submitted, meetsMinWords, onComplete]);

  const canSubmit = meetsMinWords && !exceedsMaxWords && !submitted;

  // Keyboard shortcuts: Ctrl+Enter / Cmd+Enter submits (web only, no-op on native)
  useKeyboardShortcut('Enter', handleSubmit, { ctrl: true, enabled: canSubmit });
  useKeyboardShortcut('Enter', handleSubmit, { meta: true, enabled: canSubmit });

  const wordCountColor = exceedsMaxWords
    ? '#DC2626'
    : meetsMinWords
      ? '#059669'
      : theme.textSecondary;

  return (
    <Box
      p="$4"
      rounded="$lg"
      style={{ backgroundColor: theme.card, borderWidth: 1, borderColor: theme.border }}
    >
      <VStack space="md">
        {/* Prompt */}
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.text, ...ffs }}
        >
          {prompt}
        </Text>

        {/* Writing type badge */}
        <Box alignSelf="flex-start" px="$2" py="$1" rounded="$sm" style={{ backgroundColor: theme.primarySoft }}>
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: theme.primary, fontWeight: '600', ...ffs }}
          >
            {writingType}
          </Text>
        </Box>

        {hint && !submitted && (
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, fontStyle: 'italic', ...ffs }}
          >
            {hint}
          </Text>
        )}

        {/* Target vocabulary */}
        {targetVocabulary && targetVocabulary.length > 0 && !submitted && (
          <VStack space="xs">
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, fontWeight: '600', ...ffs }}
            >
              Try to use these words:
            </Text>
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_XS * fontScale, color: theme.primary, ...ffs }}
            >
              {targetVocabulary.join(' · ')}
            </Text>
          </VStack>
        )}

        {/* Scaffolding toggle */}
        {scaffolding && scaffolding.length > 0 && !submitted && (
          <VStack space="xs">
            <TouchableOpacity
              testID="freewrite-scaffolding-toggle"
              accessibilityLabel={showScaffolding ? 'Hide writing hints' : 'Show writing hints'}
              accessibilityRole="button"
              onPress={() => setShowScaffolding(!showScaffolding)}
              activeOpacity={0.7}
            >
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_XS * fontScale, color: theme.primary, fontWeight: '500', ...ffs }}
              >
                {showScaffolding ? '▾ Hide writing hints' : '▸ Show writing hints'}
              </Text>
            </TouchableOpacity>
            {showScaffolding && (
              <Box
                p="$2"
                rounded="$md"
                style={{ backgroundColor: theme.isDark ? '#374151' : '#F3F4F6' }}
              >
                <VStack space="xs">
                  {scaffolding.map((item, idx) => (
                    <Text
                      key={idx}
                      allowFontScaling={false}
                      style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}
                    >
                      • {item}
                    </Text>
                  ))}
                </VStack>
              </Box>
            )}
          </VStack>
        )}

        {/* Text input */}
        {!submitted && (
          <TextInput
            testID="freewrite-input"
            accessibilityLabel="Write your response"
            accessibilityRole="text"
            value={userText}
            onChangeText={setUserText}
            placeholder="Start writing..."
            placeholderTextColor={theme.textSecondary}
            multiline
            style={{
              backgroundColor: theme.inputBg,
              borderWidth: 1,
              borderColor: exceedsMaxWords ? '#DC2626' : theme.border,
              borderRadius: 8,
              padding: 12,
              fontSize: FS_SM * fontScale,
              color: theme.text,
              minHeight: 120,
              textAlignVertical: 'top',
              ...ffs,
            }}
            allowFontScaling={false}
          />
        )}

        {/* Word counter */}
        {!submitted && (
          <Text
            allowFontScaling={false}
            style={{
              fontSize: FS_XS * fontScale,
              color: wordCountColor,
              textAlign: 'right',
              fontWeight: '500',
              ...ffs,
            }}
          >
            {wordCount}/{minWords}–{maxWords} words
          </Text>
        )}

        {/* Submit button */}
        {!submitted && (
          <TouchableOpacity
            testID="freewrite-submit"
            accessibilityLabel="Submit writing"
            accessibilityRole="button"
            onPress={handleSubmit}
            disabled={!meetsMinWords || exceedsMaxWords}
            activeOpacity={0.7}
          >
            <Box
              py="$3"
              rounded="$md"
              alignItems="center"
              style={{
                backgroundColor: meetsMinWords && !exceedsMaxWords ? colors.primary : (theme.isDark ? '#374151' : '#E5E7EB'),
              }}
            >
              <Text
                allowFontScaling={false}
                style={{
                  fontSize: FS_SM * fontScale,
                  fontWeight: '600',
                  color: meetsMinWords && !exceedsMaxWords ? '#FFFFFF' : theme.textSecondary,
                  ...ffs,
                }}
              >
                Submit Writing
              </Text>
            </Box>
          </TouchableOpacity>
        )}

        {/* Submitted result */}
        {submitted && (
          <VStack space="sm">
            {/* User's text displayed */}
            <Box
              p="$3"
              rounded="$md"
              style={{ backgroundColor: theme.inputBg, borderWidth: 1, borderColor: theme.border }}
            >
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, color: theme.text, ...ffs }}
              >
                {userText}
              </Text>
            </Box>

            <Box
              p="$2"
              rounded="$md"
              style={{ backgroundColor: '#D1FAE5' }}
            >
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: '#065F46', ...ffs }}
              >
                ✓ Writing submitted ({wordCount} words)
              </Text>
            </Box>

            {/* Sample answer toggle */}
            {sampleAnswer && (
              <VStack space="xs">
                <TouchableOpacity
                  testID="freewrite-sample-toggle"
                  accessibilityLabel={showSample ? 'Hide sample answer' : 'Show sample answer'}
                  accessibilityRole="button"
                  onPress={() => setShowSample(!showSample)}
                  activeOpacity={0.7}
                >
                  <Text
                    allowFontScaling={false}
                    style={{ fontSize: FS_XS * fontScale, color: theme.primary, fontWeight: '500', ...ffs }}
                  >
                    {showSample ? '▾ Hide sample answer' : '▸ Compare with sample answer'}
                  </Text>
                </TouchableOpacity>
                {showSample && (
                  <Box
                    p="$3"
                    rounded="$md"
                    style={{ backgroundColor: theme.isDark ? '#1E3A5F' : '#EFF6FF', borderWidth: 1, borderColor: theme.primary }}
                  >
                    <Text
                      allowFontScaling={false}
                      style={{ fontSize: FS_SM * fontScale, color: theme.text, fontStyle: 'italic', ...ffs }}
                    >
                      {sampleAnswer}
                    </Text>
                  </Box>
                )}
              </VStack>
            )}
          </VStack>
        )}
      </VStack>
    </Box>
  );
});
